/**
 * Utilities for the quick CD configuration step of the "Configurar CD desde
 * IaC" flow. CD templates dedicated to that flow (e.g.
 * helm-deploy-gke-plataforma) mark a handful of fields of their
 * editor_schema.json with the custom keyword `x-quick-config`; the UI extracts
 * just those fields into a minimal schema and renders them right after the
 * user picks an infrastructure, before the full editor form.
 */

export const QUICK_CONFIG_KEYWORD = 'x-quick-config';

export interface QuickConfigExtraction {
  /** Minimal JSON Schema: object root whose properties are the sections that
   * contain marked fields, each keeping only those fields. */
  schema: Record<string, any>;
  /** Total number of marked fields across all sections. */
  fieldCount: number;
}

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Locates the `configValues` schema node wherever it sits in the editor
 * schema tree. The API wraps the template's own schema under
 * `properties.repositoryTemplate` (plus optional `metadata`), so the search
 * is recursive rather than assuming an exact nesting.
 */
function findConfigValuesNode(node: unknown): Record<string, any> | undefined {
  if (!isPlainObject(node)) return undefined;
  const props = node.properties;
  if (!isPlainObject(props)) return undefined;
  if (isPlainObject(props.configValues)) return props.configValues;
  for (const key of Object.keys(props)) {
    const found = findConfigValuesNode(props[key]);
    if (found) return found;
  }
  return undefined;
}

/**
 * Extracts the fields marked with `x-quick-config` from a template editor
 * schema into a minimal standalone JSON Schema, preserving section structure,
 * titles, defaults and (filtered) required lists. Field order within a
 * section follows numeric keyword values first (ascending), then declaration
 * order for boolean marks. Returns null when the schema has no marked fields
 * or cannot be traversed.
 */
export function extractQuickConfigSchema(
  editorSchema: unknown,
): QuickConfigExtraction | null {
  try {
    const configValues = findConfigValuesNode(editorSchema);
    if (!isPlainObject(configValues) || !isPlainObject(configValues.properties)) {
      return null;
    }

    const sections: Record<string, any> = {};
    const requiredSections: string[] = [];
    let fieldCount = 0;

    for (const [sectionKey, sectionNode] of Object.entries(
      configValues.properties,
    )) {
      if (!isPlainObject(sectionNode) || !isPlainObject(sectionNode.properties)) {
        continue;
      }

      const marked = Object.entries(sectionNode.properties).filter(
        ([, field]) =>
          isPlainObject(field) && Boolean(field[QUICK_CONFIG_KEYWORD]),
      ) as Array<[string, Record<string, any>]>;
      if (marked.length === 0) continue;

      const orderOf = (field: Record<string, any>) =>
        typeof field[QUICK_CONFIG_KEYWORD] === 'number'
          ? (field[QUICK_CONFIG_KEYWORD] as number)
          : Number.POSITIVE_INFINITY;
      marked.sort(([, a], [, b]) => orderOf(a) - orderOf(b));

      const pickedProps: Record<string, any> = {};
      for (const [name, field] of marked) pickedProps[name] = field;

      const sectionRequired = Array.isArray(sectionNode.required)
        ? sectionNode.required.filter(
            (r: unknown): r is string => typeof r === 'string' && r in pickedProps,
          )
        : [];

      sections[sectionKey] = {
        type: 'object',
        ...(sectionNode.title ? { title: sectionNode.title } : {}),
        properties: pickedProps,
        ...(sectionRequired.length > 0 ? { required: sectionRequired } : {}),
      };
      if (sectionRequired.length > 0) requiredSections.push(sectionKey);
      fieldCount += marked.length;
    }

    if (fieldCount === 0) return null;

    return {
      schema: {
        type: 'object',
        properties: sections,
        ...(requiredSections.length > 0 ? { required: requiredSections } : {}),
      },
      fieldCount,
    };
  } catch {
    return null;
  }
}

/**
 * Deep-merges `toMerge` into `obj` wherever a key of `toMerge` already exists
 * in `obj`, at any depth (same semantics as the inline merge historically used
 * by DynamicFormGeneratorFieldCD for infra outputs). Keys of `toMerge` with no
 * match anywhere in `obj` are dropped. Returns a new object; inputs are not
 * mutated.
 */
export function deepMergeAtMatchingKeys(
  obj: any,
  toMerge: Record<string, any> | null | undefined,
): any {
  if (!isPlainObject(obj) || !isPlainObject(toMerge)) return obj;
  const result: Record<string, any> = { ...obj };
  const pending: Record<string, any> = { ...toMerge };

  // Merge keys found directly at this level first
  for (const key of Object.keys(pending)) {
    if (key in result) {
      result[key] = isPlainObject(result[key])
        ? { ...result[key], ...pending[key] }
        : pending[key];
      delete pending[key];
    }
  }

  // Recurse into nested objects for any remaining unmerged keys
  for (const k of Object.keys(result)) {
    if (Object.keys(pending).length === 0) break;
    if (isPlainObject(result[k])) {
      result[k] = deepMergeAtMatchingKeys(result[k], pending);
    }
  }

  return result;
}

/**
 * Builds the {section: values} prefill from the infra's Terraform outputs
 * (`artifactoryOutputs`), mirroring what DynamicFormGeneratorFieldCD applies
 * to the form: takes the first application entry and keeps every section
 * whose `.value` is a plain object. Returns null when there is nothing usable.
 */
export function buildOutputsPrefill(
  artifactoryOutputs: Record<string, any> | null | undefined,
): Record<string, any> | null {
  if (!isPlainObject(artifactoryOutputs)) return null;
  const appIdKey = Object.keys(artifactoryOutputs)[0];
  if (!appIdKey) return null;
  const appOutputs = artifactoryOutputs[appIdKey];
  if (!isPlainObject(appOutputs)) return null;

  const prefill: Record<string, any> = {};
  for (const sectionKey of Object.keys(appOutputs)) {
    const section = appOutputs[sectionKey];
    if (isPlainObject(section) && isPlainObject(section.value)) {
      prefill[sectionKey] = section.value;
    }
  }
  return Object.keys(prefill).length > 0 ? prefill : null;
}

// NOTE: the headless payload builder (buildCdFormPayload) that used to live
// here was removed: the IaC flow now builds the payload SERVER-SIDE via
// PUT /repositories/:repo/templates/from-infra (see ADR-0002 in the workspace
// docs), so the browser neither receives the infra outputs nor replicates the
// defaults/merge logic. buildOutputsPrefill/deepMergeAtMatchingKeys stay: the
// manual Artifactory flow (DynamicFormGeneratorFieldCD) still uses them.

/**
 * View-resolution strategy for loading an EXISTING CD configuration for
 * editing (as opposed to creating a new one). A config created through the
 * infra-driven flow only ever exposed its `x-quick-config` fields to the
 * user; everything else (`x-infra-output`/`x-infra-secret` fields plus fixed
 * `readOnly` template metadata like `infra.context_id`) was resolved/generated
 * server-side and the user never saw or chose it. Re-opening such a config
 * must therefore show only the fields the user actually had available,
 * regardless of the fact the full stored object contains much more.
 *
 * IMPORTANT: the schema embedded in `GET /repo-config/:repo/cd`'s response
 * (`data.repositoryTemplate.properties.configValues`) is NOT a reliable
 * source for this decision — in practice it preserves standard JSON-Schema
 * keywords (`type`/`const`/`default`/`readOnly`/`title`) but drops the
 * custom `x-quick-config`/`x-infra-output`/`x-infra-secret` vendor
 * extensions, presumably because the external configuration API
 * regenerates/normalizes the schema rather than forwarding the template's
 * `editor_schema.json` verbatim. The only place those keywords are known to
 * survive is `GET /template-payload/:template/:version` (`.editorSchema`) —
 * the same endpoint `QuickCdConfigForm` already trusts during creation — so
 * that is the ONLY source used here to decide which fields are editable.
 *
 * - `full`: config not created from infra (no `values.infra.context_id`) —
 *   show everything, exactly as before this feature existed.
 * - `quick-only`: infra-driven config whose authoritative template schema
 *   declares at least one `x-quick-config` field — show only those.
 * - `no-editable-fields`: infra-driven config whose authoritative template
 *   schema was resolved but declares NO `x-quick-config` field at all.
 * - `full-fallback`: infra-driven config whose authoritative template schema
 *   could NOT be resolved (template identity missing, template/version not
 *   found, network error). Safe degrade: show everything from the embedded
 *   schema rather than risk hiding real editable content behind an
 *   incorrect "nothing to configure" message.
 */
export type EditableCdConfigMode = 'full' | 'quick-only' | 'no-editable-fields' | 'full-fallback';

export interface EditableCdConfigView {
  mode: EditableCdConfigMode;
  /** Schema to render, or null when there is nothing to show (`no-editable-fields`). */
  schema: Record<string, any> | null;
  /** Values to seed the form with, matching `schema`'s shape (a strict subset of the full stored values in `quick-only` mode). */
  formData: Record<string, any> | null;
}

/** True when `values` carries the trace of having been built by the
 * infra-driven flow (`PUT .../templates/from-infra`, see `InfraCdService.ts`). */
export function isInfraDrivenCdValues(values: unknown): boolean {
  return isPlainObject(values) && Boolean((values as any).infra?.context_id);
}

/**
 * Reads the persisted template's `{ name, version }` identity from the
 * embedded schema's `template` section (a sibling of `configValues`, whose
 * `name`/`version` fields are declared as JSON-Schema `const`s — see
 * `editor_schema.json`). Used to fetch the authoritative editor schema for
 * an infra-driven config. Returns null when either value is missing.
 */
export function readCdTemplateIdentity(
  data: unknown,
): { name: string; version: string } | null {
  const templateProps = isPlainObject(data)
    ? (data as any)?.repositoryTemplate?.properties?.template?.properties
    : undefined;
  if (!isPlainObject(templateProps)) return null;
  const nameNode = templateProps.name;
  const versionNode = templateProps.version;
  const name = isPlainObject(nameNode) ? nameNode.const ?? nameNode.default : undefined;
  const version = isPlainObject(versionNode) ? versionNode.const ?? versionNode.default : undefined;
  if (typeof name !== 'string' || !name || typeof version !== 'string' || !version) return null;
  return { name, version };
}

/**
 * Picks, from the complete stored values, only the values that fall under a
 * (`extractQuickConfigSchema`-shaped) filtered schema's sections/fields.
 * Missing keys are simply omitted (the rendered `<Form>` falls back to the
 * schema's own `default`), never invented.
 */
function pickValuesForSchema(
  fullValues: unknown,
  filteredSchema: Record<string, any> | null | undefined,
): Record<string, any> {
  const picked: Record<string, any> = {};
  if (!isPlainObject(fullValues) || !isPlainObject(filteredSchema?.properties)) {
    return picked;
  }
  for (const [sectionKey, sectionSchema] of Object.entries(filteredSchema!.properties)) {
    if (!isPlainObject(sectionSchema) || !isPlainObject((sectionSchema as any).properties)) continue;
    const fullSection = (fullValues as any)[sectionKey];
    if (!isPlainObject(fullSection)) continue;
    const pickedSection: Record<string, any> = {};
    for (const fieldKey of Object.keys((sectionSchema as any).properties)) {
      if (fieldKey in fullSection) pickedSection[fieldKey] = fullSection[fieldKey];
    }
    if (Object.keys(pickedSection).length > 0) picked[sectionKey] = pickedSection;
  }
  return picked;
}

/**
 * Resolves how an existing CD config GET response (`{ repositoryTemplate,
 * values }`, as returned by `GET /repo-config/:repo/cd`) should be presented
 * for editing. Pure/side-effect-free so it is unit-testable on its own.
 * `authoritativeEditorSchema` must be fetched separately by the caller (see
 * `useEditableCdConfigView`) from `GET /template-payload/:template/:version`
 * — this function does not perform any I/O itself. The caller is
 * responsible for keeping the complete `values` around (this function never
 * discards data, only decides what to *show*) so it can be reconstructed on
 * save via `deepMergeAtMatchingKeys`.
 */
export function resolveEditableCdConfigView(
  data: unknown,
  authoritativeEditorSchema: unknown | null,
): EditableCdConfigView {
  const values = isPlainObject(data) ? (data as any).values : undefined;
  const fullSchema = isPlainObject(data)
    ? (data as any)?.repositoryTemplate?.properties?.configValues ?? null
    : null;

  if (!isInfraDrivenCdValues(values)) {
    return { mode: 'full', schema: fullSchema, formData: isPlainObject(values) ? values : null };
  }

  if (!authoritativeEditorSchema) {
    return { mode: 'full-fallback', schema: fullSchema, formData: isPlainObject(values) ? values : null };
  }

  const extraction = extractQuickConfigSchema(authoritativeEditorSchema);
  if (!extraction || extraction.fieldCount === 0) {
    return { mode: 'no-editable-fields', schema: null, formData: null };
  }

  return {
    mode: 'quick-only',
    schema: extraction.schema,
    formData: pickValuesForSchema(values, extraction.schema),
  };
}

function flattenLeafPaths(obj: unknown, prefix = ''): Array<[string, any]> {
  if (!isPlainObject(obj)) return [];
  const result: Array<[string, any]> = [];
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value)) {
      result.push(...flattenLeafPaths(value, path));
    } else {
      result.push([path, value]);
    }
  }
  return result;
}

function getAtPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce((acc: any, key) => (isPlainObject(acc) ? acc[key] : undefined), obj);
}

/**
 * Defensive guard for the "reconstruct the full config before saving" step
 * (Propuesta 3): after `deepMergeAtMatchingKeys(fullValues, editedVisible)`,
 * verifies every leaf value the user could see/edit actually made it into
 * the merged result unchanged. `deepMergeAtMatchingKeys` silently drops keys
 * it cannot find anywhere in the target object (e.g. because a template's
 * schema shape changed between the version that created the config and the
 * one now declaring `x-quick-config`), which would otherwise silently
 * discard a user's edit instead of persisting it. Returns the dot-paths that
 * failed to reconcile (empty array when everything is fine); the caller
 * should block the save and surface these paths rather than persist.
 */
export function findUnreconciledEditableFields(
  edited: Record<string, any> | null | undefined,
  merged: Record<string, any> | null | undefined,
): string[] {
  if (!isPlainObject(edited)) return [];
  const mismatches: string[] = [];
  for (const [path, value] of flattenLeafPaths(edited)) {
    const mergedValue = getAtPath(merged, path);
    if (JSON.stringify(mergedValue) !== JSON.stringify(value)) {
      mismatches.push(path);
    }
  }
  return mismatches;
}
